import moment from 'moment-timezone';

import Request from 'files/Request.js';

/*
 * Appointment
 *
 * The one place an in-home evaluation request leaves the website. The lead
 * quiz ends here, and there are two ways through:
 *
 *   1. Our own calendar (the default). The open times arrive with the
 *      dealership's preferences, the visitor picks one, and a single request
 *      goes to AFT. A visitor who wants a call instead sends the same request
 *      with demo_request set to false.
 *
 *   2. Calendly, for dealers who have linked it on Global Data. Calendly owns
 *      the booking and Global Data's webhook creates the lead and demo when it
 *      lands, so nothing is sent from here on that path.
 *
 * What AFT does with a request is its business, not the page's: dealers with
 * aft.replicated_websites.demo_requests have it forwarded to Global Data as a
 * lead (and a demo request, when one was made), everyone else gets an email.
 * The page says the same thing either way, because either way somebody from
 * the dealership is about to be in touch.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * The contract:
 *
 *   POST https://aftplatform.com/api/v2/dealerships/
 *   {
 *     type: 'new_replicated_website_contact_submission',
 *     dealership_id: 431,
 *     demo_request: true,                  // false: a call-back, no demo request
 *     date: '2026-09-16 10:00:00' | null,  // dealership local time, null when demo_request is false
 *     customer: {
 *       first_name, last_name, email_address, phone_number,
 *       address: { street_address_1, locality, administrative_area_level_1, postal_code, country },
 *       location: { latitude, longitude } | null
 *     },
 *     message: '...',                      // the quiz walkthrough, or what the visitor wrote
 *     source: { form, url, referrer, web_alias }  // form: 'lead_quiz' | 'contact'
 *   }
 *   -> 200 { result: 'submitted' }
 *
 * Open times, on the dealership preferences for Global Data dealers:
 *
 *   appointments: {
 *     timezone: 'America/Chicago',
 *     availability: {
 *       '2026-09-15': ['09:00:00', '13:00:00'],
 *       '2026-09-16': ['10:00:00']
 *     }
 *   }
 *
 * Only open times, dealership local. Everyone else gets the standard window
 * below. Present but empty means fully booked: no calendar, and the visitor is
 * offered a call-back instead.
 * ────────────────────────────────────────────────────────────────────────────
 */

// The web alias is the first path segment when a dealership is served from a
// shared domain, and absent when they are on their own.
const getWebAlias = () => {
    let segment = String(window.location.pathname || '').split('/').filter(Boolean)[0];
    return segment || null;
};

/*
 * The dealer's Calendly setup, or null when the default calendar should be
 * used. Arrives on the dealership as preferences.calendly.
 *
 * The url is held to calendly.com because it becomes an iframe on the page —
 * a mistyped or hostile value should fall back to our calendar, not embed
 * someone else's site.
 */
export const getCalendly = dealership => {
    let config = dealership?.calendly;
    if(config?.enabled !== true || typeof config.url !== 'string') {
        return null;
    }
    try {
        let url = new URL(config.url);
        if(url.protocol !== 'https:' || /(^|\.)calendly\.com$/.test(url.hostname) === false) {
            return null;
        }
    } catch(e) {
        return null;
    }
    return config;
};

/*
 * Calendly custom answers, from what we already know about the visitor.
 *
 * Calendly only takes custom answers by position (a1 is the first custom
 * question on the event type, a10 the tenth), so the dealer's config has to
 * say which question is which: { phone_number: 'a1', notes: 'a3' }. Anything
 * unmapped is simply not prefilled.
 */
export const getCalendlyCustomAnswers = (config, values) => {
    let answers = {};
    Object.entries(config?.custom_answers || {}).forEach(([field, key]) => {
        if(/^a([1-9]|10)$/.test(key) && values[field]) {
            answers[key] = values[field];
        }
    });
    return answers;
};

/*
 * The standard window, for dealers who do not keep availability on Global
 * Data: the next two weeks, Monday to Saturday, on the hour from 9am to 5pm.
 * The visitor is choosing when they would like somebody to come rather than
 * claiming a slot, and the dealer confirms it.
 */
const STANDARD_WINDOW = {
    days: 14,
    firstHour: 9,
    lastHour: 17,
    weekdays: [1, 2, 3, 4, 5, 6]
};

const getStandardAppointments = dealership => {
    let availability = {};
    for(let offset = 1; offset <= STANDARD_WINDOW.days; offset++) {
        let day = moment().add(offset, 'days');
        if(STANDARD_WINDOW.weekdays.includes(day.day()) === false) {
            continue;
        }
        let times = [];
        for(let hour = STANDARD_WINDOW.firstHour; hour <= STANDARD_WINDOW.lastHour; hour++) {
            times.push(`${String(hour).padStart(2, '0')}:00:00`);
        }
        availability[day.format('YYYY-MM-DD')] = times;
    }
    return { availability, timezone: dealership?.timezone || null };
};

// Preferences carry `appointments` only for Global Data dealers. Present but
// empty means fully booked, which is different from not keeping a calendar.
const getAppointments = dealership => {
    return dealership?.appointments || getStandardAppointments(dealership);
};

export const getTimezone = dealership => {
    let timezone = getAppointments(dealership).timezone;
    return timezone && moment.tz.zone(timezone) ? timezone : null;
};

/*
 * Days that still have an open time, in date order. The preferences were
 * fetched when the page loaded, so times that have since passed are dropped
 * here — judged against the dealership's clock, since they are its times.
 */
export const getOpenDays = dealership => {

    let availability = getAppointments(dealership).availability || {};
    let zone = getTimezone(dealership) || moment.tz.guess();
    let now = moment.tz(zone);

    return Object.keys(availability).sort().map(date => ({
        date,
        times: (Array.isArray(availability[date]) ? availability[date] : []).filter(time => {
            return moment.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm:ss', zone).isAfter(now);
        }).sort()
    })).filter(day => day.times.length > 0);
};

/*
 * What is wrong with the visitor's details, by field. 'missing' is shown as a
 * red border and nothing else, because an empty box explains itself. Anything
 * else is a sentence, because a red border around an email address that looks
 * fine to the person who typed it does not.
 */
export const getCustomerProblems = (customer, { requireAddress = true, requireMessage = false, requirePhone = true } = {}) => {

    let problems = {};
    let text = key => (customer[key] || '').trim();

    ['firstName', 'lastName'].forEach(key => {
        if(!text(key)) {
            problems[key] = 'missing';
        }
    });

    if(!text('email')) {
        problems.email = 'missing';
    } else if(/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(text('email')) === false) {
        problems.email = "That email address doesn't look quite right.";
    }

    if(!text('phone')) {
        if(requirePhone) {
            problems.phone = 'missing';
        }
    } else if(text('phone').replace(/[^0-9]/g, '').length < 10) {
        problems.phone = "That phone number looks like it's missing a few digits.";
    }

    // an evaluation happens at the house, so it has to be somewhere a person
    // can drive to - a town picked from the list is not
    if(requireAddress) {
        if(!customer.address) {
            problems.address = 'missing';
        } else if(!customer.address.street_address_1) {
            problems.address = 'Please choose your full street address, not just the town.';
        }
    }

    if(requireMessage && !text('message')) {
        problems.message = 'missing';
    }

    return problems;
};

/*
 * Sends the request. `appointment` is { date: 'YYYY-MM-DD', time: 'HH:mm:ss' }
 * for a chosen time, or null for a call-back or a general message. `form` says
 * which form it came from, 'lead_quiz' or 'contact'.
 */
export const submit = async (utils, { appointment, customer, dealership, form, message }) => {
    return await Request.aft.post(utils, '/dealerships/', {
        customer: {
            address: customer.address ? {
                administrative_area_level_1: customer.address.administrative_area_level_1,
                country: customer.address.country,
                locality: customer.address.locality,
                postal_code: customer.address.postal_code,
                street_address_1: customer.address.street_address_1
            } : null,
            email_address: customer.email?.trim(),
            first_name: customer.firstName?.trim(),
            last_name: customer.lastName?.trim(),
            location: customer.address?.location || null,
            phone_number: customer.phone?.replace(/[^0-9+]/g, '')
        },
        date: appointment ? `${appointment.date} ${appointment.time}` : null,
        dealership_id: dealership.id,
        demo_request: appointment ? true : false,
        message: message?.trim() || null,
        source: {
            form: form || null,
            referrer: document.referrer || null,
            url: window.location.href,
            web_alias: getWebAlias()
        },
        type: 'new_replicated_website_contact_submission'
    });
};

const Appointment = {
    getCalendly,
    getCalendlyCustomAnswers,
    getCustomerProblems,
    getOpenDays,
    getTimezone,
    submit
};

export default Appointment;
